import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Home.css';
import { collection, getDoc, getDocs, query, where, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { FiEye, FiShoppingCart, FiHeart } from 'react-icons/fi';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// 简单轮播组件
const Carousel: React.FC<{ items: any[] }> = ({ items }) => {
  const [current, setCurrent] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items.length) return null;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent(prev => (prev - 1 + items.length) % items.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent(prev => (prev + 1) % items.length);
  };

  const handleMouseEnter = () => {
    const timer = setTimeout(() => setShowTooltip(true), 1000);
    setTooltipTimer(timer);
  };

  const handleMouseLeave = () => {
    if (tooltipTimer) clearTimeout(tooltipTimer);
    setShowTooltip(false);
  };

  return (
    <div
      className="carousel-section"
      style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 32px auto',
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        marginTop: 70,
      }}
    >
      {items.map((item, idx) => (
        <div
          key={item.id}
          style={{
            display: idx === current ? 'block' : 'none',
            width: '100%',
            height: 380,
            position: 'relative',
            transition: 'opacity 0.6s'
          }}
        >
          <img
            src={item.imageUrl}
            alt={item.title}
            title={item.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: 'pointer'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          {/* 悬停显示 title/description 浮层 */}
          {showTooltip && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
                background: 'rgba(0,0,0,0.75)',
                color: '#fff',
                padding: '24px 32px',
                borderRadius: 12,
                zIndex: 10,
                minWidth: 260,
                maxWidth: 400,
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 10 }}>{items[current].title}</div>
              <div style={{ fontSize: 16 }}>{items[current].description}</div>
            </div>
          )}
        </div>
      ))}
      {/* 左右箭头 */}
      {items.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.85)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            aria-label="Previous"
          >
            <FaChevronLeft size={20} color="#222" />
          </button>
          <button
            onClick={handleNext}
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.85)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            aria-label="Next"
          >
            <FaChevronRight size={20} color="#222" />
          </button>
        </>
      )}
      {/* 指示点 */}
      <div style={{
        position: 'absolute',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 8
      }}>
        {items.map((_, idx) => (
          <span
            key={idx}
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: idx === current ? '#27ae60' : '#fff',
              opacity: idx === current ? 1 : 0.6,
              border: '1.5px solid #27ae60',
              cursor: 'pointer'
            }}
            onClick={() => setCurrent(idx)}
          />
        ))}
      </div>
    </div>
  );
};

type Category = {
  id: string;
  name: string;
  parentId: string;
  index?: number;
};

type Product = {
  id: string;
  title: string;
  price: number;
  images: string[];
  variants: any[];
  createdAt?: any; // 添加createdAt字段用于排序
};

const Home: React.FC = () => {
  const [carouselItems, setCarouselItems] = useState<any[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const carouselRef = useRef<any[]>([]);
  const newArrivalsRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const [roomCategoryIds, setRoomCategoryIds] = useState<{ [key: string]: string }>({});
  const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const [promotionContent, setPromotionContent] = useState<string | null>(null);

  // 首次访问首页时，若 promotion 的 isVisible 为 true 则显示弹窗提醒（弹窗内容来自 byAppointmentOnly.content）
  // 优先使用 Header 已缓存的 promotion，避免重复读取 Firestore
  useEffect(() => {
    const checkAndShowPromotion = async () => {
      const seen = localStorage.getItem('home_promotion_popup_seen');
      if (seen === 'true') return;

      try {
        const cached = localStorage.getItem('promotion');
        const cacheTime = localStorage.getItem('promotion_time');
        const now = Date.now();
        const maxAge = 10 * 60 * 1000; // 与 Header 一致 10 分钟
        if (cached && cacheTime && now - parseInt(cacheTime) < maxAge) {
          const promo = JSON.parse(cached);
          const content = promo.byAppointmentOnly?.content ?? '';
          if (promo.isVisible && content) {
            setPromotionContent(content);
            setShowPromotionPopup(true);
          }
          return;
        }
        const promotionDoc = await getDoc(doc(db, 'promotions', 'main'));
        if (promotionDoc.exists()) {
          const data = promotionDoc.data();
          const isVisible = data.isVisible || false;
          const content = data.byAppointmentOnly?.content || '';
          if (isVisible && content) {
            setPromotionContent(content);
            setShowPromotionPopup(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch promotion for popup:', error);
      }
    };
    checkAndShowPromotion();
  }, []);

  const handleClosePromotionPopup = () => {
    localStorage.setItem('home_promotion_popup_seen', 'true');
    setShowPromotionPopup(false);
  };

  // 处理房间分类点击
  const handleRoomClick = (roomType: string) => {
    const categoryId = roomCategoryIds[roomType];
    if (categoryId) {
      // 使用window.location.href确保从页面顶部开始，与Header保持一致
      window.location.href = `/category/${categoryId}`;
    }
  };

  // 清除缓存功能（开发时使用）
  const clearCache = () => {
    localStorage.removeItem('home_products');
    localStorage.removeItem('home_products_time');
    localStorage.removeItem('carousel');
    localStorage.removeItem('carousel_time');
    localStorage.removeItem('room_category_ids');
    localStorage.removeItem('room_category_ids_time');
    localStorage.removeItem('home_promotion_popup_seen');
    localStorage.removeItem('profiles');
    localStorage.removeItem('profiles_time');
    localStorage.removeItem('promotion');
    localStorage.removeItem('promotion_time');
    Object.keys(localStorage).filter(k => k.startsWith('profile_')).forEach(k => localStorage.removeItem(k));
    console.log('🗑️ 所有缓存已清除');
    // 重新加载页面以应用更改
    window.location.reload();
  };

  // 开发模式下添加清除缓存按钮（可选）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // 添加清除缓存按钮到控制台
      (window as any).clearHomeCache = clearCache;
      console.log('🔧 开发模式：使用 window.clearHomeCache() 清除缓存');
    }
  }, []);

  // 获取房间分类ID - 优化Firebase读取
  const fetchRoomCategoryIds = async () => {
    try {
      // 1. 先从 localStorage 读取，延长缓存时间
      const cached = localStorage.getItem('room_category_ids');
      const cacheTime = localStorage.getItem('room_category_ids_time');
      const now = Date.now();
      const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
      const maxAge = 24 * 60 * 60 * 1000; // 24小时缓存，分类变化很少

      if (cached && cacheAge < maxAge) {
        try {
          const roomIds = JSON.parse(cached);
          setRoomCategoryIds(roomIds);
          console.log('✅ 使用缓存的房间分类数据，节省Firebase读取');
          return;
        } catch { }
      }

      // 2. 只有在缓存过期或不存在时才请求 Firestore
      console.log('📡 从Firebase获取房间分类数据...');
      const q = query(collection(db, 'categories'));
      const snapshot = await getDocs(q);
      const categories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category));

      // 查找房间分类
      const livingRoom = categories.find(cat =>
        cat.name?.toLowerCase().includes('living') ||
        cat.name?.toLowerCase().includes('客厅')
      );
      const diningRoom = categories.find(cat =>
        cat.name?.toLowerCase().includes('dining') ||
        cat.name?.toLowerCase().includes('餐厅')
      );
      const bedroom = categories.find(cat =>
        cat.name?.toLowerCase().includes('bedroom') ||
        cat.name?.toLowerCase().includes('卧室')
      );

      const roomIds = {
        'living-room': livingRoom?.id || '',
        'dining-room': diningRoom?.id || '',
        'bedroom': bedroom?.id || ''
      };

      setRoomCategoryIds(roomIds);

      // 3. 缓存房间分类ID
      localStorage.setItem('room_category_ids', JSON.stringify(roomIds));
      localStorage.setItem('room_category_ids_time', Date.now().toString());
      console.log('💾 房间分类数据已缓存，下次访问将使用缓存');
    } catch (error) {
      console.error('Failed to fetch room categories:', error);
    }
  };

  // 获取新品 - 懒加载版本，优化Firebase读取
  const fetchProducts = useCallback(async () => {
    if (productsLoading) return;

    setProductsLoading(true);
    try {
      // 1. 先从 localStorage 读取，优先秒开，再按需刷新
      const cached = localStorage.getItem('home_products');
      const cacheTime = localStorage.getItem('home_products_time');
      const now = Date.now();
      const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
      const maxAge = 15 * 60 * 1000; // 15分钟缓存，兼顾新鲜度与读取成本
      const refreshThreshold = 2 * 60 * 1000; // 2分钟内缓存不额外刷新，进一步减少读取

      if (cached && cacheAge < maxAge) {
        try {
          const productList = JSON.parse(cached);
          setProducts(productList);
          setProductsLoaded(true);
          console.log('✅ 使用缓存的产品数据，节省Firebase读取');
          // 缓存非常新时直接返回，避免每次都读 Firestore
          if (cacheAge < refreshThreshold) return;
        } catch { }
      }

      // 2. 只有在缓存过期或不存在时才请求 Firestore
      console.log('📡 从Firebase获取产品数据...');
      const q = query(
        collection(db, 'products'),
        where('visible', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const productList: Product[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // 排除Used Furniture分类的产品
        const categories = data.category || [];
        const hasUsedFurniture = categories.some((cat: any) =>
          cat === 'Used Furniture' || cat === 'used furniture'
        );

        const hasValidImage =
          Array.isArray(data.images) &&
          data.images.length > 0 &&
          typeof data.images[0] === 'string' &&
          data.images[0].trim() !== '';

        if (!hasUsedFurniture && data.title && typeof data.price === 'number' && hasValidImage) {
          productList.push({
            id: doc.id,
            title: data.title,
            price: data.price,
            images: data.images || [],
            variants: data.variants || [],
            createdAt: data.createdAt, // 保存Firebase Timestamp用于排序
          });
        }
      });

      // 按createdAt排序，处理Firebase Timestamp格式
      productList.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // 降序排列，最新的在前
      });

      // 取最新的8个产品
      const finalProductList = productList.slice(0, 8);
      setProducts(finalProductList);
      setProductsLoaded(true);

      // 3. 缓存产品数据
      localStorage.setItem('home_products', JSON.stringify(finalProductList));
      localStorage.setItem('home_products_time', Date.now().toString());
      console.log('💾 产品数据已缓存，下次访问将使用缓存');
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setProductsLoading(false);
    }
  }, [productsLoading]);

  useEffect(() => {
    fetchRoomCategoryIds();
  }, []);

  useEffect(() => {
    // 获取轮播图数据 - 优化Firebase读取
    const fetchCarousel = async () => {
      // 1. 先从 localStorage 读取，延长缓存时间
      const cached = localStorage.getItem('carousel');
      const cacheTime = localStorage.getItem('carousel_time');
      const now = Date.now();
      const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
      const maxAge = 24 * 60 * 60 * 1000; // 24小时缓存，轮播图变化不频繁

      if (cached && cacheAge < maxAge) {
        try {
          const items = JSON.parse(cached);
          carouselRef.current = items;
          setCarouselItems(items);
          console.log('✅ 使用缓存的轮播图数据，节省Firebase读取');
          return;
        } catch { }
      }

      // 2. 只有在缓存过期或不存在时才请求 Firestore
      console.log('📡 从Firebase获取轮播图数据...');
      const docRef = doc(db, 'website', 'carousel');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (Array.isArray(data.items)) {
          const filteredItems = data.items.filter((item: any) => item.active && item.imageUrl);
          // 3. 比较字符串，有变化才更新
          const itemsStr = JSON.stringify(filteredItems);
          const refStr = JSON.stringify(carouselRef.current);
          if (itemsStr !== refStr) {
            carouselRef.current = filteredItems;
            setCarouselItems(filteredItems);
            localStorage.setItem('carousel', JSON.stringify(filteredItems));
            localStorage.setItem('carousel_time', Date.now().toString());
            console.log('💾 轮播图数据已缓存，下次访问将使用缓存');
          }
        }
      }
    };
    fetchCarousel();
  }, []);

  // Intersection Observer for lazy loading New Arrivals
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !productsLoaded && !productsLoading) {
            fetchProducts();
          }
        });
      },
      {
        rootMargin: '100px', // 提前100px开始加载
        threshold: 0.1
      }
    );

    const currentRef = newArrivalsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [fetchProducts, productsLoaded, productsLoading]);

  return (
    <div className="home-container">
      {/* 首次访问 promotion 弹窗 */}
      {showPromotionPopup && promotionContent && (
        <div className="promotion-popup-overlay" onClick={handleClosePromotionPopup} role="dialog" aria-modal="true" aria-labelledby="promotion-popup-title">
          <div className="promotion-popup" onClick={e => e.stopPropagation()}>
            <h3 id="promotion-popup-title" className="promotion-popup-title">Notice</h3>
            <p className="promotion-popup-content">{promotionContent}</p>
            <p className="promotion-popup-contact">
              To schedule an appointment, please call or email:
              <br />
              <a href="tel:+14162978088">(416) 297-8088</a>
              <br />
              <a href="mailto:gracioushome.ca@gmail.com">gracioushome.ca@gmail.com</a>
            </p>
            <button className="promotion-popup-btn" onClick={handleClosePromotionPopup}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Carousel Section */}
      <Carousel items={carouselItems} />

      {/* How to Order Section */}
      <section className="how-to-order">
        <div className="how-to-order-header">
          <h2>How to Order</h2>
        </div>
        <div className="how-to-order-image">
          <img src="/how_to_order.png" alt="How to Order" />
        </div>
        <div className="how-to-order-steps">
          <div className="step">
            <div className="step-icon">🖥️</div>
            <div className="step-text">
              <span className="step-number">STEP 1</span>
              <span className="step-title">Shop Furniture & Add to Cart</span>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">🛒</div>
            <div className="step-text">
              <span className="step-number">STEP 2</span>
              <span className="step-title">Complete the Order Info</span>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">🏠</div>
            <div className="step-text">
              <span className="step-number">STEP 3</span>
              <span className="step-title">Sumbit the Order & We will get in touch with you</span>
            </div>
          </div>
        </div>
      </section>


      {/* Logo Version Section */}
      <section className="room-categories logo-version">
        <div className="room-categories-header">
          <h2>Our Room Collections</h2>
          <p>Professional furniture solutions for every space</p>
        </div>
        <div className="room-grid">
          <div className="room-card logo-card" onClick={() => handleRoomClick('living-room')}>
            <div className="room-image">
              <img src="/livingroom.png" alt="Living Room Logo" className="room-logo-only" />
            </div>
            <div className="room-content">
              <h3>Living Room</h3>
              <p>Create a welcoming space for family and friends</p>
              <button className="room-btn" onClick={(e) => { e.stopPropagation(); handleRoomClick('living-room'); }}>Shop Living Room</button>
            </div>
          </div>

          <div className="room-card logo-card" onClick={() => handleRoomClick('dining-room')}>
            <div className="room-image">
              <img src="/diningroom.png" alt="Dining Room Logo" className="room-logo-only" />
            </div>
            <div className="room-content">
              <h3>Dining Room</h3>
              <p>Elegant dining solutions designed for unforgettable meals</p>
              <button className="room-btn" onClick={(e) => { e.stopPropagation(); handleRoomClick('dining-room'); }}>Shop Dining Room</button>
            </div>
          </div>

          <div className="room-card logo-card" onClick={() => handleRoomClick('bedroom')}>
            <div className="room-image">
              <img src="/bedroom.png" alt="Bedroom Logo" className="room-logo-only" />
            </div>
            <div className="room-content">
              <h3>Bedroom</h3>
              <p>Transform your bedroom into a peaceful retreat</p>
              <button className="room-btn" onClick={(e) => { e.stopPropagation(); handleRoomClick('bedroom'); }}>Shop Bedroom</button>
            </div>
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="new-arrivals" ref={newArrivalsRef}>
        <h2>New Arrivals</h2>
        {productsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading new arrivals...</p>
          </div>
        ) : (
          <div className="product-grid">
            {products.map((product) => {
              const variantImages = product.variants?.map((v: any) => v.image).filter(Boolean) || [];
              const allImages = [...variantImages, ...(product.images || [])];
              const mainImg = allImages[0] || 'https://via.placeholder.com/150';

              return (
                <div
                  className="product-card"
                  key={product.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <img
                    src={mainImg}
                    alt={product.title}
                  />
                  <div className="product-actions">
                    <button
                      className="action-btn"
                      title="Quick Shop"
                      onClick={e => e.stopPropagation()}
                    >
                      <FiEye />
                    </button>
                    <button
                      className="action-btn"
                      title="Add to Cart"
                      onClick={e => e.stopPropagation()}
                    >
                      <FiShoppingCart />
                    </button>
                    <button
                      className="action-btn"
                      title="Add to Wishlist"
                      onClick={e => e.stopPropagation()}
                    >
                      <FiHeart />
                    </button>
                  </div>
                  <p>{product.title}</p>
                  <p style={{ fontWeight: 500 }}>${product.price}/month</p>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;